import React from "react";
import { Box, Typography } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useScrollToSection from "../../hooks/useScrollToSection";
import { useTranslation } from "react-i18next";
import useLang from "../../hooks/useLang";

const linkSx = {
  display: "block",
  fontSize: "0.9rem",
  fontWeight: 400,
  fontFamily: '"Calibri", sans-serif',
  color: "rgba(29, 47, 65, 0.65)",
  textDecoration: "none",
  mb: 1.5,
  transition: "color 0.2s",
  cursor: "pointer",
  background: "none",
  border: "none",
  padding: 0,
  textAlign: "left" as const,
  "&:hover": { color: "#1D2F41" },
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const currentYear = new Date().getFullYear();
  const scrollToSection = useScrollToSection();
  const navigate = useNavigate();
  const location = useLocation();
  const homePath = `/${lang}`;

  const NAV_COLUMNS = [
    {
      heading: t("footer.services"),
      links: [
        { label: t("footer.links.digitalTransformation"), sectionId: "solutions", capabilityIndex: 0 },
        { label: t("footer.links.disruptiveTechnology"), sectionId: "solutions", capabilityIndex: 1 },
        { label: t("footer.links.platformEngineering"), sectionId: "solutions", capabilityIndex: 2 },
        { label: t("footer.links.dataCloud"), sectionId: "solutions", capabilityIndex: 3 },
      ],
    },
    {
      heading: t("footer.company"),
      links: [
        { label: t("footer.links.about"), sectionId: "about", capabilityIndex: undefined },
        { label: t("footer.links.contact"), sectionId: "contact", capabilityIndex: undefined },
      ],
    },
  ];

  const handleNavClick = (sectionId: string, capabilityIndex?: number) => {
    if (capabilityIndex !== undefined) {
      window.dispatchEvent(new CustomEvent("capability:open", { detail: capabilityIndex }));
    }
    if (location.pathname !== homePath) {
      navigate(homePath, { state: { sectionId, capabilityIndex } });
    } else {
      scrollToSection(sectionId);
    }
  };

  return (
    <Box component="footer" sx={{ bgcolor: "#f8f9fb" }}>
      {/* Main footer */}
      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: { md: "320px" },
        }}
      >
        {/* Left */}
        <Box
          sx={{
            width: { md: "40%" },
            flexShrink: 0,
            borderRight: { md: "1px solid rgba(29,47,65,0.15)" },
            px: { xs: 3, md: 6 },
            py: { xs: 8, md: 10 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                display: "inline-block",
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: '"Calibri", sans-serif',
                color: "#1D2F41",
                bgcolor: "#EBEDF0",
                px: 1.25,
                py: 0.75,
                mb: 2,
              }}
            >
              {t("footer.tagline")}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "2.2rem", md: "2.8rem" },
                fontWeight: 700,
                letterSpacing: "-0.025em",
                lineHeight: 1.15,
                color: "#1D2F41",
                fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
                mb: 5,
              }}
            >
              {t("footer.headline")}
            </Typography>
          </Box>

          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  fontFamily: '"Calibri", sans-serif',
                  color: "#1D2F41",
                  minWidth: 80,
                }}
              >
                {t("footer.contact")}
              </Typography>
              <Box
                sx={{
                  width: "1px",
                  height: 14,
                  bgcolor: "rgba(29,47,65,0.25)",
                }}
              />
              <Typography
                component="a"
                href="mailto:info@kanonics.com"
                sx={{
                  fontSize: "0.85rem",
                  fontFamily: '"Calibri", sans-serif',
                  color: "rgba(29,47,65,0.65)",
                  textDecoration: "none",
                  "&:hover": { color: "#1D2F41" },
                }}
              >
                info@kanonics.com
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  fontFamily: '"Calibri", sans-serif',
                  color: "#1D2F41",
                  minWidth: 80,
                }}
              >
                {t("footer.location")}
              </Typography>
              <Box
                sx={{
                  width: "1px",
                  height: 14,
                  bgcolor: "rgba(29,47,65,0.25)",
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontFamily: '"Calibri", sans-serif',
                  color: "rgba(29,47,65,0.65)",
                }}
              >
                {t("footer.locationValue")}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: { xs: 3, md: 8 },
            py: { xs: 8, md: 10 },
            display: "flex",
            gap: { xs: 6, md: 10 },
            flexWrap: "wrap",
          }}
        >
          {NAV_COLUMNS.map((col) => (
            <Box key={col.heading} sx={{ minWidth: 160 }}>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  fontFamily: '"Calibri", sans-serif',
                  color: "#1D2F41",
                  mb: 3,
                }}
              >
                {col.heading}
              </Typography>
              {col.links.map((link) => (
                <Typography
                  key={link.label}
                  component="button"
                  onClick={() => handleNavClick(link.sectionId, link.capabilityIndex)}
                  sx={linkSx}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom bar */}
      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          px: { xs: 3, md: 6 },
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontFamily: '"Calibri", sans-serif',
            color: "rgba(29,47,65,0.4)",
          }}
        >
          Kanonics GmbH {currentYear}
        </Typography>
        <Box sx={{ display: "flex", gap: 4 }}>
          {[
            { label: t("footer.dataSecurity"), to: `/${lang}/data-security` },
            { label: t("footer.impressum"), to: `/${lang}/impressum` },
          ].map((item) => (
            <Typography
              key={item.label}
              component={Link}
              to={item.to}
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                fontFamily: '"Calibri", sans-serif',
                color: "rgba(29,47,65,0.45)",
                textDecoration: "none",
                "&:hover": { color: "#1D2F41" },
                transition: "color 0.2s",
              }}
            >
              {item.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
